import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Menu, X, ShoppingCart, User, BookOpen, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const navLinks = [
    { href: "/philosophy", label: "Philosophy" },
    { href: "/pillars", label: "8 Pillars" },
    { href: "/library", label: "Library" },
    { href: "/sessions", label: "1:1 Recalibration" },
    { href: "/courses", label: "Courses" },
    { href: "/about", label: "About" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="nav-logo"
          >
            <img 
              src="/somalume-logo.png" 
              alt="Somalume" 
              className="w-10 h-10 object-contain"
            />
            <span className="font-heading text-xl text-[#EAEAEA] tracking-tight hidden sm:block">
              Somalume
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`font-body text-sm tracking-wide uppercase transition-colors duration-300 ${
                  isActive(link.href)
                    ? "text-gold"
                    : "text-[#A1A1AA] hover:text-[#EAEAEA]"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <Link 
                  to="/cart" 
                  data-testid="nav-cart"
                  className="p-2 text-[#A1A1AA] hover:text-gold transition-colors duration-300"
                >
                  <ShoppingCart size={20} />
                </Link>
                <Link 
                  to="/dashboard" 
                  data-testid="nav-dashboard"
                  className="p-2 text-[#A1A1AA] hover:text-gold transition-colors duration-300"
                >
                  <BookOpen size={20} />
                </Link>
                <div className="flex items-center gap-3 pl-4 border-l border-white/10">
                  <span className="text-sm text-[#A1A1AA]">{user.name}</span>
                  <button
                    onClick={handleLogout}
                    data-testid="nav-logout"
                    className="p-2 text-[#A1A1AA] hover:text-gold transition-colors duration-300"
                  >
                    <LogOut size={18} />
                  </button>
                </div>
              </>
            ) : (
              <Link to="/auth" data-testid="nav-signin">
                <Button variant="outline" className="border-gold/30 text-gold hover:bg-gold/10 uppercase text-xs tracking-widest">
                  Sign In
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-[#A1A1AA]"
            data-testid="nav-mobile-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-white/5" data-testid="nav-mobile-menu">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`font-body text-sm tracking-wide uppercase py-2 ${
                    isActive(link.href) ? "text-gold" : "text-[#A1A1AA]"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {user ? (
                <>
                  <Link to="/cart" onClick={() => setIsOpen(false)} className="text-[#A1A1AA] py-2 flex items-center gap-2">
                    <ShoppingCart size={18} /> Cart
                  </Link>
                  <Link to="/dashboard" onClick={() => setIsOpen(false)} className="text-[#A1A1AA] py-2 flex items-center gap-2">
                    <BookOpen size={18} /> My Library
                  </Link>
                  <button onClick={handleLogout} className="text-[#A1A1AA] py-2 flex items-center gap-2">
                    <LogOut size={18} /> Sign Out
                  </button>
                </>
              ) : (
                <Link to="/auth" onClick={() => setIsOpen(false)}>
                  <Button className="w-full bg-gold text-[#050505] hover:bg-gold-accent uppercase text-xs tracking-widest mt-2">
                    Sign In
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
